/**
 * Shown in place of a feature the tenant's plan does not include.
 *
 * Deliberately an upsell rather than a dead end: it names the feature, says
 * which plan unlocks it, and sends the tenant to the contact form with the
 * matching `?interest=` topic preselected.
 */

import { useEntitlements } from "@/contexts/EntitlementsContext";

/**
 * @param {{ feature: string, title?: string, description?: string }} props
 */
export default function FeatureLocked({ feature, title, description }) {
  const { lockInfo, planLabel } = useEntitlements();
  const info = lockInfo(feature);
  if (!info) return null;

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg rounded-brand-lg border border-slate-200 bg-white p-8 text-center shadow-card dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300">
          <i className="ri-lock-2-line text-xl" aria-hidden="true" />
        </div>

        <h2 className="mb-2 text-xl text-slate-900 dark:text-slate-100">
          {title || `${info.label} is part of ${info.requiredPlanLabel}`}
        </h2>

        <p className="mb-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {description ||
            `Your workspace is on the ${planLabel} plan. Upgrade to ${info.requiredPlanLabel} to turn on ${info.label}.`}
        </p>

        <p className="mb-6 text-xs text-slate-500 dark:text-slate-500">
          Your existing conversations and settings stay exactly as they are.
        </p>

        <a
          href={info.contactUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-brand bg-teal-600 px-6 text-sm font-semibold text-white transition hover:bg-teal-700"
        >
          Discuss upgrading
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}
